/**
 * Layout 컴포넌트
 *
 * 앱의 공통 레이아웃을 담당합니다.
 * - 헤더: 로고 + 언어 토글
 * - 메인 컨텐츠 영역
 * - 푸터: 기술 스택 정보
 *
 * 간소화된 구조:
 * - 불필요한 네비게이션 제거 (모든 기능이 홈페이지에 있음)
 * - 언어 전환 버튼 추가 (EN/KO)
 */

import { Music, Github } from 'lucide-react'
import { useI18n, type Language } from '../i18n'

// =============================================================================
// 타입 정의
// =============================================================================

interface LayoutProps {
  /** 메인 컨텐츠 (페이지 컴포넌트) */
  children: React.ReactNode
}

// =============================================================================
// 메인 컴포넌트
// =============================================================================

export function Layout({ children }: LayoutProps) {
  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------

  const { language, setLanguage, t } = useI18n()

  // ---------------------------------------------------------------------------
  // 언어 토글 핸들러
  // ---------------------------------------------------------------------------

  /**
   * 언어 전환
   *
   * 현재 언어의 반대로 전환합니다.
   * ko → en, en → ko
   */
  const toggleLanguage = () => {
    const newLang: Language = language === 'ko' ? 'en' : 'ko'
    setLanguage(newLang)
  }

  // ---------------------------------------------------------------------------
  // 렌더링
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen flex flex-col bg-dark-900">
      {/* ===================================================================== */}
      {/* 헤더 */}
      {/* ===================================================================== */}
      <header className="bg-dark-800/80 backdrop-blur-md border-b border-dark-500/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex justify-between items-center h-14">

            {/* ------------------------------------------------------------- */}
            {/* 로고 */}
            {/* ------------------------------------------------------------- */}
            <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              {/* 아이콘 */}
              <div className="w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center">
                <Music className="w-4 h-4 text-white" />
              </div>
              {/* 텍스트 */}
              <span className="text-lg font-bold text-text-primary">
                {t('appName')}
              </span>
            </a>

            {/* ------------------------------------------------------------- */}
            {/* 우측 액션 버튼들 */}
            {/* ------------------------------------------------------------- */}
            <div className="flex items-center gap-2">

              {/* 언어 토글 버튼 */}
              <button
                onClick={toggleLanguage}
                className="px-3 py-1.5 text-sm font-medium rounded-lg
                         bg-dark-600 hover:bg-dark-500 transition-colors
                         text-text-secondary"
                title={language === 'ko' ? 'Switch to English' : '한국어로 전환'}
              >
                {/* 현재 언어 표시 (클릭하면 반대 언어로 전환) */}
                {language === 'ko' ? 'EN' : '한국어'}
              </button>

              {/* GitHub 링크 */}
              <a
                href="https://github.com/seonooy"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-text-muted hover:text-text-primary transition-colors"
                title="GitHub"
              >
                <Github className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* ===================================================================== */}
      {/* 메인 컨텐츠 */}
      {/* ===================================================================== */}
      <main className="flex-1">
        {children}
      </main>

    </div>
  )
}
